import React, { useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ImportData() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const inputRef = useRef(null);

  const doReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-data", { confirm: confirmText });
      const d = data.deleted;
      toast.success(`Data dihapus: ${d.santri} santri, ${d.orang_tua} orang tua, ${d.tagihan} tagihan, ${d.produk} produk.`);
      setResetOpen(false);
      setConfirmText("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setResetting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_import_santri.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Gagal mengunduh template");
    }
  };

  const submit = async () => {
    if (!file) return toast.error("Pilih file Excel (.xlsx) dulu");
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.students_created} santri & ${data.parents_created} orang tua ditambahkan`);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Impor Data"
        subtitle="Tambah banyak santri & orang tua sekaligus dari file Excel (.xlsx)."
        action={
          <Button onClick={downloadTemplate} variant="outline" data-testid="download-template-button" className="rounded-xl">
            <Download size={18} className="mr-2" /> Unduh Template
          </Button>
        }
      />

      <div className="max-w-2xl">
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
          <label
            htmlFor="xlsx"
            className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-stone-300 rounded-2xl py-12 cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-all"
          >
            <FileSpreadsheet size={40} className="text-emerald-600" />
            <p className="text-stone-600 font-medium">{file ? file.name : "Klik untuk pilih file .xlsx"}</p>
            <p className="text-xs text-stone-400">Format kolom fleksibel — lihat template</p>
            <input
              id="xlsx"
              ref={inputRef}
              type="file"
              accept=".xlsx"
              data-testid="import-file-input"
              className="hidden"
              onChange={(e) => setFile(e.target.files[0])}
            />
          </label>

          <Button onClick={submit} disabled={loading || !file} data-testid="import-submit-button" className="w-full mt-4 py-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-base font-semibold">
            {loading ? <Loader2 className="animate-spin" /> : <><Upload size={18} className="mr-2" /> Proses Impor</>}
          </Button>
        </div>

        {result && (
          <div className="mt-5 bg-white border border-stone-200 rounded-2xl p-6 shadow-sm animate-fade-up" data-testid="import-result">
            <div className="flex items-center gap-2 text-emerald-700 mb-4">
              <CheckCircle2 size={20} /> <p className="font-heading font-semibold">Impor Selesai</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl bg-emerald-50 p-4 text-center">
                <p className="font-heading text-3xl font-bold text-emerald-700">{result.students_created}</p>
                <p className="text-xs text-stone-500 mt-1">Santri baru</p>
              </div>
              <div className="rounded-xl bg-blue-50 p-4 text-center">
                <p className="font-heading text-3xl font-bold text-blue-700">{result.parents_created}</p>
                <p className="text-xs text-stone-500 mt-1">Orang tua baru</p>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3">
                <p className="flex items-center gap-2 text-amber-700 text-sm font-medium mb-2">
                  <AlertTriangle size={15} /> Catatan ({result.errors.length})
                </p>
                <ul className="text-xs text-amber-800 space-y-1 list-disc list-inside">
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Zona Berbahaya */}
      <div className="max-w-2xl mt-8">
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 text-rose-700 mb-1">
            <AlertTriangle size={18} /> <p className="font-heading font-semibold">Zona Berbahaya</p>
          </div>
          <p className="text-sm text-stone-600 mb-4">
            Hapus <b>semua data demo/uji coba</b> (santri, orang tua, tagihan, produk, penjualan, riwayat pembayaran) untuk memulai dari awal dengan data asli. Akun <b>admin</b> & <b>kasir</b> tidak terhapus. Tindakan ini <b>tidak bisa dibatalkan</b>.
          </p>
          <Button onClick={() => setResetOpen(true)} data-testid="reset-data-button" className="bg-rose-600 hover:bg-rose-700 rounded-xl">
            <Trash2 size={18} className="mr-2" /> Hapus Semua Data
          </Button>
        </div>
      </div>

      <Dialog open={resetOpen} onOpenChange={(o) => { if (!o) { setResetOpen(false); setConfirmText(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-rose-700">Hapus Semua Data?</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-1">
            <p className="text-sm text-stone-600">
              Semua santri, orang tua, tagihan, produk, dan riwayat transaksi akan dihapus permanen. Ketik <b>HAPUS</b> untuk mengonfirmasi.
            </p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="reset-confirm-input"
              placeholder="Ketik HAPUS"
              className="rounded-xl"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetOpen(false); setConfirmText(""); }} className="rounded-xl">Batal</Button>
            <Button onClick={doReset} disabled={confirmText !== "HAPUS" || resetting} data-testid="confirm-reset-button" className="bg-rose-600 hover:bg-rose-700 rounded-xl">
              {resetting ? <Loader2 className="animate-spin" /> : "Hapus Permanen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
